import React from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Pagination from "react-bootstrap/Pagination";
import { TableSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import { Search } from "lucide-react";

const SkorProtectMetricsTable = ({
  data,
  loading,
  total,
  currentPage,
  limit,
  onPageChange,
  onViewDetails,
  searchText,
  onSearchChange,
}) => {
  const totalPages = Math.ceil(total / limit);
  const indexOfFirstItem = (currentPage - 1) * limit + 1;
  const indexOfLastItem = Math.min(currentPage * limit, total);

  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => onPageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Showing <strong>{total > 0 ? indexOfFirstItem : 0}</strong> to{" "}
          <strong>{indexOfLastItem}</strong> of{" "}
          <strong>{total}</strong> landlords
        </div>
        <Pagination className="tenants-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <div className="tenants-container metrics-theme-table">
      <div className="tenants-card">
        <div className="p-4 d-flex justify-content-between align-items-center">
          <p className="metrics-table-caption mb-0">
            SKOR PROTECT METRICS — LANDLORDS WITH ACTIVE PROTECTIONS
          </p>
          <div className="position-relative" style={{ width: "260px" }}>
            <Search
              size={14}
              className="position-absolute text-muted"
              style={{ left: "10px", top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search name, email, company..."
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{ paddingLeft: "30px" }}
            />
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <TableSkeleton rows={limit} cols={4} />
          ) : (
            <Table responsive hover className="tenants-table mb-0">
              <thead>
                <tr>
                  <th>LANDLORD</th>
                  <th>WORKSPACE</th>
                  <th className="text-center">PROTECTIONS</th>
                  <th className="text-center">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((item, index) => (
                    <tr key={item.landlord_id || index}>
                      <td>
                        <div className="tenant-name-stack">
                          <span className="tenant-name">
                            {item.landlord?.fname_companyName}{" "}
                            {item.landlord?.lname}
                          </span>
                          <span className="tenant-email">
                            {item.landlord?.email || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        {item.workspaces && item.workspaces.length > 0 ? (
                          <>
                            <div className="fw-500">
                              {item.workspaces.length}{" "}
                              {item.workspaces.length === 1 ? "Workspace" : "Workspaces"}
                            </div>
                            {item.workspaces.length > 0 && (
                              <div
                                className="text-muted small"
                                style={{ fontSize: "10px" }}
                              >
                                {item.workspaces
                                  .slice(0, 2)
                                  .map((ws) => ws?.name || "Unknown")
                                  .join(", ")}
                                {item.workspaces.length > 2 &&
                                  ` +${item.workspaces.length - 2} more`}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="fw-500">N/A</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="fw-600">
                          {item.protect_count || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="white"
                          className="tenant-action-btn"
                          onClick={() => onViewDetails(item)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-5 text-muted">
                      No Data Found!
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        <div className="p-3">
          {totalPages > 1 && !loading && total > 0 && renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default SkorProtectMetricsTable;
